import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactElement, type ReactNode } from 'react';

/**
 * A tiny path router over the `ADMIN_ROUTES` names (S111; D319 adds one-segment params). It maps each route name to
 * a real URL and back and syncs with the browser history — enough for the operator console without a routing
 * dependency. A path may carry `:param` segments (`/passengers/:id` → `{ id }`). Access control stays in
 * `@vello/app-shell`'s deny-by-default guards; this only reflects the resolved route in the URL.
 */
export const ROUTE_PATHS: Readonly<Record<string, string>> = {
  login: '/login',
  recovery: '/recovery',
  recovery_reset: '/recovery/reset',
  // D449 (A-05/A-06): the invitation landing (public) and the operator's own second factor.
  invitation: '/invitation',
  my_access: '/account/access',
  // D806 (doc 35 §3.2): "Meu perfil" - the operator's own photo, name, preferences, security and sessions (opened from the operator menu).
  my_profile: '/account/profile',
  session_expired: '/session-expired',
  dashboard: '/',
  live_map: '/live-map',
  rides: '/rides',
  ride_detail: '/rides/:id',
  dispatch: '/dispatch',
  courier_jobs: '/operations/courier-jobs',
  cases: '/cases',
  case_detail: '/cases/:id',
  disputes: '/disputes',
  safety: '/safety',
  driver_directory: '/drivers/directory',
  driver_detail: '/drivers/directory/:id',
  driver_approvals: '/drivers/approvals',
  driver_documents: '/drivers/documents',
  // W9-DRIVER-REGISTRY (D481)
  driver_registry: '/drivers/registry',
  driver_register: '/drivers/registry/new',
  driver_documents_all: '/drivers/documents/all',
  provider_services: '/drivers/services', // D883 (Serviços em análise)
  // W9-REPUTATION (D478)
  reputation_suspensions: '/suspensions',
  reputation_relationships: '/relationships',
  passengers: '/passengers',
  passenger_detail: '/passengers/:id',
  companies: '/companies',
  finance: '/finance',
  finance_payouts: '/finance/payouts',
  finance_partner_billing: '/finance/partner-billing',
  tariff_categories: '/tariffs/categories',
  tariff_dynamic: '/tariffs/dynamic',
  service_categories: '/tariffs/service-catalog', // D462 W9-CATEGORY-CATALOG
  campaigns: '/campaigns',
  registries: '/registries',
  markets: '/markets',
  operators: '/settings/operators', // D449 (A-05)
  access_profiles: '/settings/access-profiles', // D475 (A-018, A-019)
  manual_access: '/settings/manual-access', // D597 (MAN-ACCESS)
  passenger_registry: '/passengers/registry', // D475 (E-001, E-005, X-09)
  providers_overview: '/providers/overview', // D513 (C-031)
  customers_overview: '/customers/overview', // D513 (C-031)
  integrations: '/integrations',
  security: '/security',
  governed_settings: '/settings/governed-rules',
  central_ai_settings: '/settings/central-ai', // D631 (Atendimento e IA)
  ai_spend: '/settings/ai-spend', // D639 (Gastos de IA)
  maps_spend: '/settings/maps-spend', // D874 (Gastos de mapas)
  platform_setup: '/settings/platform-setup', // D615 (SETUP-READINESS)
  integrations_otp_setup: '/integrations/otp-setup',
  // D429 W-EMBED-ADMIN
  integrations_widgets: '/integrations/widgets',
  // D440 W8-RIDER-SAFETY-CONSENT (X-02)
  legal_documents: '/settings/legal-documents',
  // The institutional site's job openings ("Vagas do site")
  career_openings: '/settings/careers',
  notifications: '/notifications',
  analytics: '/reports',
  audit: '/audit',
  food_ops: '/food/operations',
  food_review: '/food/onboarding-review',
  food_ads: '/food/ads-governance',
  food_fulfillment: '/food/fulfillment-policy',
  // W6-ADMIN-SCREENS (D401)
  food_orders: '/food/orders',
  food_postsale: '/food/post-sale',
  food_stores: '/food/stores',
  send_deliveries: '/send/deliveries',
  send_claims: '/send/claims',
  finance_merchant_statements: '/finance/merchant-statements',
  // W6-REFUND-FOUR-EYES (D398)
  finance_refund_approvals: '/finance/refund-approvals',
  // W7-CASH-HANDOVER-CONSOLE (D411)
  finance_cash_handovers: '/finance/cash-handovers',
  finance_driver_debts: '/finance/driver-debts', // D438 (A-02)
  finance_rider_debts: '/finance/rider-debts', // D536
  finance_rider_cash_blocks: '/finance/rider-cash-blocks', // D536
  finance_wallets: '/finance/wallets', // D459
  finance_rider_wallet: '/finance/rider-wallet', // D737b
  finance_incentive_fund: '/finance/incentive-fund', // D489b
  finance_incentive_offers: '/finance/incentive-offers', // D490
  finance_incentive_controller: '/finance/incentive-controller', // D495
  finance_coupon_campaigns: '/finance/coupon-campaigns', // D493
  referrals: '/referrals', // D723
  finance_settlement: '/finance/settlements', // D469
  driver_groups: '/drivers/groups', // D459
  four_eyes_inbox: '/approvals',
  // W7-OPS-GOVERNANCE (D420)
  ops_incidents: '/ops/incidents',
  route_anomalies: '/ops/route-anomalies', // D471 (A-29)
  rides_grid: '/rides/grid', // D472 (A-11)
  report_catalog: '/reports/catalog', // D472 (P-07)
  audit_trail: '/audit/trail', // D472 (A-14)
  risk_areas: '/ops/risk-areas', // D474 (C-024, I-076)
  alert_center: '/ops/alerts', // D474 (H-009, I-058)
  incentive_zones: '/ops/incentive-zones', // D488 (doc 17 §3.2)
  service_areas: '/ops/service-areas', // D616 (ADR-0121)
  devices: '/ops/devices', // D561 (ADR-0116)
  tv_panels: '/ops/tv-panels', // D647 (ADR-0123)
  places: '/ops/places', // D674 (doc 27)
  broadcasts: '/marketing/broadcasts', // D477 (H-014, H-015, H-016)
  banners: '/marketing/banners', // D477 (H-018, H-019)
  awards: '/drivers/awards', // D477 (F-051, I-053)
  ride_chats: '/ops/ride-chats', // D473 (H-001, H-007)
  central_teams: '/ops/central-teams', // D632 (doc 25 section 7, Q-367)
  central_whatsapp: '/ops/central-whatsapp', // D636 (doc 25 phase 1, Q-365)
  // W8-DRIVER-ELIGIBILITY (D437)
  kyc_catalog: '/settings/kyc-documents',
  driver_vehicles: '/drivers/vehicles',
  // D522 (AN-BI-UI): Análises.
  bi_panels: '/analises',
  bi_explorer: '/analises/explorar',
  bi_reports: '/analises/relatorios',
  bi_exports: '/analises/exportacoes', // D532 (AN-EXPORTS)
  bi_lists: '/analises/listas', // D527
  access_report: '/seguranca/acessos', // D527 (I-050)
  about: '/about',
};

/** The route name of an address no screen owns (it has no path of its own: it is only ever reached by an unknown URL). */
export const NOT_FOUND_ROUTE = 'not_found';

/** Routes that need no session (sign-in, recovery, session expired, invitation): the boot never moves a signed-out visitor off them. */
export const PUBLIC_ROUTES: ReadonlySet<string> = new Set(['login', 'recovery', 'recovery_reset', 'session_expired', 'invitation']);

export type RouteParams = Readonly<Record<string, string>>;

interface Match {
  readonly route: string;
  readonly params: RouteParams;
}

/** Resolve a pathname to a route + params: exact paths first, then patterns with `:param` segments. */
export function matchPath(pathname: string): Match {
  // A trailing slash is the same address (`/rides/grid/` is `/rides/grid`, not a record named "grid" of `/rides/:id`).
  if (pathname.length > 1 && pathname.endsWith('/')) return matchPath(pathname.replace(/\/+$/, ''));
  const segs = pathname.split('/').filter((s) => s !== '');
  let fallback: Match | undefined;
  for (const [name, pattern] of Object.entries(ROUTE_PATHS)) {
    if (pattern === pathname) return { route: name, params: {} };
    if (!pattern.includes(':')) continue;
    const pSegs = pattern.split('/').filter((s) => s !== '');
    if (pSegs.length !== segs.length) continue;
    const params: Record<string, string> = {};
    let ok = true;
    for (let i = 0; i < pSegs.length; i++) {
      const p = pSegs[i]!;
      const s = segs[i]!;
      if (p.startsWith(':')) {
        try {
          params[p.slice(1)] = decodeURIComponent(s);
        } catch {
          params[p.slice(1)] = s;
        }
      } else if (p !== s) {
        ok = false;
        break;
      }
    }
    if (ok && fallback === undefined) fallback = { route: name, params };
  }
  // An address no route owns is a not-found page, never the dashboard under a wrong URL (D854 L7a-020).
  return fallback ?? { route: pathname === '' ? 'dashboard' : NOT_FOUND_ROUTE, params: {} };
}

/** Build the URL for a route, substituting `:param` segments from `params` (URL-encoded). */
export function pathFor(name: string, params: RouteParams = {}): string {
  const pattern = ROUTE_PATHS[name] ?? '/';
  return pattern
    .split('/')
    .map((seg) => (seg.startsWith(':') ? encodeURIComponent(params[seg.slice(1)] ?? '') : seg))
    .join('/');
}

/** A screen's filter carried in the URL (`?person=<id>&role=RIDER`): flat, string-valued, never a secret — it reloads, goes back and is shared as a link. */
export type RouteQuery = Readonly<Record<string, string>>;

/** Parse a `location.search` into a flat {@link RouteQuery} (the first value of a repeated key wins; an empty value is dropped). */
export function parseQuery(search: string): RouteQuery {
  const out: Record<string, string> = {};
  for (const [k, v] of new URLSearchParams(search)) if (v !== '' && out[k] === undefined) out[k] = v;
  return out;
}

/** The `?a=1&b=2` text of a query (keys sorted, so the same filter is always the same URL); `''` when it is empty. */
export function queryString(query: RouteQuery = {}): string {
  const sp = new URLSearchParams();
  for (const k of Object.keys(query).sort()) if (query[k] !== undefined && query[k] !== '') sp.set(k, query[k] as string);
  const text = sp.toString();
  return text === '' ? '' : `?${text}`;
}

/** The full URL of a route: path (with its params) plus the query. */
export function urlFor(name: string, params: RouteParams = {}, query: RouteQuery = {}): string {
  return `${pathFor(name, params)}${queryString(query)}`;
}

interface RouterValue {
  readonly route: string;
  readonly params: RouteParams;
  /** The URL query of the current location (a screen's filter). */
  readonly query: RouteQuery;
  /** Go to a route; `query` carries a filter into the screen (omitted → the path alone). Pushes a history entry. */
  readonly navigate: (name: string, params?: RouteParams, query?: RouteQuery) => void;
  /**
   * "Back goes where you came from" (owner, 2026-10-18): when the previous history entry is inside the app (it pushed this one), step back
   * in the history — the list the operator came from, with its filters in the URL; otherwise (a deep link, a reload on the page) go to the
   * screen's natural list `fallbackRoute`.
   */
  readonly goBack: (fallbackRoute: string, params?: RouteParams, query?: RouteQuery) => void;
  /** Replace the query of the current route in place (no history entry): a filter the operator changed or removed. */
  readonly replaceQuery: (query: RouteQuery) => void;
}

interface Located extends Match {
  readonly query: RouteQuery;
}

const RouterContext = createContext<RouterValue | undefined>(undefined);

/** How many entries this app pushed before the current one (kept in `history.state`; 0 for the first entry of the tab, e.g. a deep link). */
const appDepth = (): number => {
  const state: unknown = window.history.state;
  const depth = typeof state === 'object' && state !== null ? (state as { readonly velloDepth?: unknown }).velloDepth : undefined;
  return typeof depth === 'number' && depth > 0 ? depth : 0;
};

const locate = (): Located => ({ ...matchPath(window.location.pathname), query: parseQuery(window.location.search) });

export function RouterProvider({ children }: { children: ReactNode }): ReactElement {
  const [match, setMatch] = useState<Located>(locate);

  useEffect(() => {
    const onPop = (): void => setMatch(locate());
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  const navigate = useCallback((name: string, params: RouteParams = {}, query?: RouteQuery): void => {
    // Without a query this is the old path-only navigation (the same path is not pushed again, so a screen's own `?s=` state stays);
    // with one, the whole URL is compared, so a different filter on the same screen is a new history entry.
    const url = query === undefined ? pathFor(name, params) : urlFor(name, params, query);
    const current = query === undefined ? window.location.pathname : `${window.location.pathname}${window.location.search}`;
    if (current !== url) window.history.pushState({ velloDepth: appDepth() + 1 }, '', url);
    setMatch({ route: ROUTE_PATHS[name] !== undefined ? name : 'dashboard', params, query: parseQuery(window.location.search) });
  }, []);

  const replaceQuery = useCallback((query: RouteQuery): void => {
    const url = `${window.location.pathname}${queryString(query)}`;
    window.history.replaceState({ velloDepth: appDepth() }, '', url);
    setMatch((prev) => ({ ...prev, query: parseQuery(queryString(query)) }));
  }, []);

  const goBack = useCallback((fallbackRoute: string, params: RouteParams = {}, query?: RouteQuery): void => {
    if (appDepth() > 0) window.history.back();
    else navigate(fallbackRoute, params, query);
  }, [navigate]);

  const value = useMemo<RouterValue>(() => ({ route: match.route, params: match.params, query: match.query, navigate, goBack, replaceQuery }), [match, navigate, goBack, replaceQuery]);
  return <RouterContext.Provider value={value}>{children}</RouterContext.Provider>;
}

export function useRouter(): RouterValue {
  const value = useContext(RouterContext);
  if (value === undefined) throw new Error('useRouter must be used within a RouterProvider');
  return value;
}
